import {useEffect, useMemo, useState} from "react";
import {useAppSelector} from "../../hooks/useAppSelector";
import {
    bootIntercom,
    buildIntercomJwtSettings,
    fetchIntercomMessengerToken,
    isIntercomSupportEnabled,
    shutdownIntercom,
    updateIntercom,
    IntercomMessengerToken,
} from "../../services/intercom";

const getCurrentHref = () => (typeof window === "undefined" ? "" : window.location.href);

function useLocationHref() {
    const [href, setHref] = useState(getCurrentHref);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const notify = () => setHref(window.location.href);
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function patchedPushState(...args: Parameters<History["pushState"]>) {
            originalPushState.apply(window.history, args);
            notify();
        } as History["pushState"];

        window.history.replaceState = function patchedReplaceState(...args: Parameters<History["replaceState"]>) {
            originalReplaceState.apply(window.history, args);
            notify();
        } as History["replaceState"];

        window.addEventListener("popstate", notify);
        window.addEventListener("hashchange", notify);

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
            window.removeEventListener("popstate", notify);
            window.removeEventListener("hashchange", notify);
        };
    }, []);

    return href;
}

const IntercomMessenger = () => {
    const user = useAppSelector(state => state.auth.user);
    const appConfig = useAppSelector(state => state.global.config);
    const href = useLocationHref();
    const enabled = isIntercomSupportEnabled(appConfig);
    const userIdentity = user?.uuid || user?.id || user?.email || null;
    const [messengerToken, setMessengerToken] = useState<IntercomMessengerToken | null>(null);
    const jwtSettings = useMemo(() => buildIntercomJwtSettings(messengerToken), [messengerToken]);

    useEffect(() => {
        let mounted = true;
        let refreshTimer: number | null = null;

        if (!enabled || !userIdentity) {
            setMessengerToken(null);
            shutdownIntercom();
            return;
        }

        setMessengerToken(null);
        shutdownIntercom();

        const refreshToken = () => {
            fetchIntercomMessengerToken()
                .then((token) => {
                    if (!mounted) return;

                    setMessengerToken(token);
                    const expiresInSeconds = token.expires_in || 600;
                    const refreshInMs = Math.max(60, expiresInSeconds - 60) * 1000;
                    refreshTimer = window.setTimeout(refreshToken, refreshInMs);
                })
                .catch((error) => {
                    if (mounted) {
                        setMessengerToken(null);
                        shutdownIntercom();
                    }
                    if (process.env.NODE_ENV !== "production") {
                        console.warn("Unable to fetch Intercom Messenger token", error?.message || error);
                    }
                });
        };

        refreshToken();

        return () => {
            mounted = false;
            if (refreshTimer) {
                window.clearTimeout(refreshTimer);
            }
        };
    }, [enabled, userIdentity]);

    useEffect(() => {
        if (!enabled || !jwtSettings) return;

        bootIntercom(jwtSettings, appConfig);
    }, [appConfig, enabled, jwtSettings]);

    useEffect(() => {
        if (!enabled || !jwtSettings) return;

        updateIntercom(jwtSettings, appConfig);
    }, [appConfig, enabled, href, jwtSettings]);

    useEffect(() => {
        if (!enabled || !userIdentity) return;
        if (!messengerToken?.user_id) return;
        if (messengerToken.user_id === userIdentity) return;

        shutdownIntercom();
    }, [enabled, messengerToken?.user_id, userIdentity]);

    useEffect(() => {
        return () => shutdownIntercom();
    }, []);

    return null;
};

export default IntercomMessenger;
